import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AdminShell } from './AdminShell';
import { adminApi, AdminCompany, Paged } from '@/lib/adminApi';

function PlanBadge({ plan }: { plan: string }) {
  const styles: Record<string, string> = {
    free: 'bg-ink-100 text-ink-700 border-ink-200',
    growth: 'bg-accent-50 text-accent-700 border-accent-200',
    global: 'bg-ink-900 text-white border-ink-900',
    enterprise: 'bg-amber-100 text-amber-900 border-amber-200',
  };
  return (
    <span
      className={
        'inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ' +
        (styles[plan] ?? 'bg-ink-100 text-ink-700 border-ink-200')
      }
    >
      {plan}
    </span>
  );
}

export function AdminCompaniesPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const list = useQuery<Paged<AdminCompany>>({
    queryKey: ['admin', 'companies', search],
    queryFn: () => adminApi.listCompanies({ search: search || undefined, limit: 100 }),
  });

  const rows = list.data?.items ?? [];

  return (
    <AdminShell titleKey="admin.companies.title">
      <div className="mb-4 flex items-center gap-3">
        <input
          type="search"
          placeholder={t('admin.companies.search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-80 rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        {typeof list.data?.total === 'number' && (
          <span className="text-xs text-ink-500">
            {t('admin.common.total')}: {list.data.total.toLocaleString()}
          </span>
        )}
      </div>

      {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}

      {!list.isLoading && rows.length === 0 && (
        <p className="text-sm text-ink-500">{t('admin.companies.empty')}</p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('admin.companies.col_name')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.companies.col_country')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.companies.col_plan')}</th>
                <th className="py-2.5 px-3 font-medium">
                  {t('admin.companies.col_verification')}
                </th>
                <th className="py-2.5 px-3 font-medium">{t('admin.companies.col_seats')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.companies.col_ai_cap')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.companies.col_created')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-ink-100">
                  <td className="py-2.5 px-3 font-medium text-ink-900">{c.name}</td>
                  <td className="py-2.5 px-3 text-ink-700 num">{c.country_code}</td>
                  <td className="py-2.5 px-3">
                    <PlanBadge plan={c.plan} />
                  </td>
                  <td className="py-2.5 px-3 text-ink-700 text-xs">{c.verification_status}</td>
                  <td className="py-2.5 px-3 num text-ink-700">{c.seat_count}</td>
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {c.ai_monthly_cap_cents != null
                      ? `¥${Math.round(c.ai_monthly_cap_cents / 100).toLocaleString()}`
                      : '—'}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
